import { Button, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

type GhostButtonProps = {
  onClick?: () => void;
  buttonText: string;
  isLoading?: boolean;
  disabled?: boolean;
  additionalProps?: object;
  sxProps?: object;
  variant?: 'text' | 'outlined' | 'contained';
};

const GhostButton = ({
  onClick,
  buttonText,
  isLoading = false,
  disabled = false,
  additionalProps = {},
  sxProps = {},
  variant = 'outlined',
}: GhostButtonProps) => {
  const [loading, setLoading] = useState(false);
  const { theme } = useThemeContext();

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const defaultSxProps = {
    borderRadius: '24px',
    textTransform: 'none',
    padding: '8px 24px',
    borderColor: theme.buttons.ghost.borderColor,
    color: theme.buttons.ghost.color,
    border: '2px solid',
    backgroundColor: 'transparent',
    '&:hover': {
      color: theme.buttons.ghost.hover.color,
      borderColor: theme.buttons.ghost.hover.borderColor,
      border: '2px solid',
      backgroundColor: 'transparent',
    },
    '&.Mui-disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  };

  const mergedDisabled = loading || disabled || (additionalProps as any)?.disabled === true;

  return (
    <Button
      variant={variant}
      onClick={onClick}
      disabled={mergedDisabled}
      sx={{
        ...defaultSxProps,
        ...sxProps,
      }}
      {...additionalProps}
    >
      {loading ? <CircularProgress size={24} color="inherit" /> : buttonText}
    </Button>
  );
};

GhostButton.propTypes = {
  onClick: PropTypes.func,
  buttonText: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  additionalProps: PropTypes.object,
  sxProps: PropTypes.object,
  variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
};

export default GhostButton;
